import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLaptopCode, faGraduationCap, faHeadphonesSimple, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import includes from 'lodash/includes';
import get from 'lodash/get';
import classNames from 'classnames';
import { connect } from 'react-redux';
import differenceInDays from 'date-fns/difference_in_days';
import { Col } from 'reactstrap';

import AddToCartBtn from 'Components/Button/Cart/AddToCartCardBtn';
import store from 'Redux/store';
import DlcBadge from 'Components/Label/DlcBadge';
import ProductCover from 'Components/Media/Image/ProductCover';
import HitCardInCart from 'Components/Card/HitCard/HitCardInCart';
import CardLabel from 'Components/Label/CardLabel';
import Drm from 'Components/ProductInfo/Drm';
import PriceContainer from 'Components/Price/PriceContainer';
import FlashCardProgress from 'Components/Progress/FlashCardProgress';
import { toggleCheckoutModal } from 'Redux/ducks/modal';
import FormatProductName from 'Helpers/Format/FormatProductName';
import HitLinkTo from 'Helpers/Product/HitLinkTo';
import useViewport from 'Hooks/useViewport';
import imageSizes from 'Helpers/Image/image-sizes';
import NewCountdownTimer from 'Components/Timer/CountdownTimer/CountdownTimer';
import { useHitCardPopup } from 'Context/HitCardPopupContext';

// The least the popup may overhang the card by, in total across both sides. Cards are grid
// columns, so they grow with the container at wider breakpoints; this stops a card outgrowing
// the popup and showing around its edges. The widths below are what the popup normally uses.
const MIN_POPUP_OVERHANG = 48;
const MIN_POPUP_WIDTH = 400;
const MIN_POPUP_WIDTH_BIG_CARD = 490;
// Space is tight at lg, so the popup stays at a fixed width rather than tracking the card.
const POPUP_WIDTH_LG = 340;

function PopupHitCard({
  hit,
  siteTheme,
  lgSize,
  mdSize,
  smSize,
  xsSize,
  bigCard,
  notAlgolia,
  flashDeal,
  showFlashDealBar, // Allows regular hits to show bar as the flashDeal prop changes data
  hitCardLightTheme,
  hitCardDarkTheme,
  lazy,
  hideDesc,
  classes,
  sizesOverride,
  place,
  giftButton,
  labelOverride,
  hitCardContainerTag,
  clickFunction,
  inLibrary,
  inSteamLibrary,
  authenticated,
  coverTypeOverride,
  adaptiveSize,
  bundleCoverGrid,
}) {
  const cardRef = useRef(null);
  const { isAboveSm, isBelowLg, isLg } = useViewport();
  const { showPopup, setPopupPosition, scheduleHide } = useHitCardPopup();
  const { locale } = window;
  let startingDate = hit.available_valid_from;
  let endingDate = hit.available_valid_until;
  let OS = hit.operating_systems || [];
  const discountPercentOverride = hit.discount_percent_override;
  const hoverTimeout = useRef(null);
  const isBigCard = lgSize === 4;

  // Navigating away unmounts the card but not the popup, which lives at the app root. Without
  // this a hover that is still counting down opens a popup for a card that has already gone.
  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  const isSrpBundle = get(hit, 'is_srp_bundle', false);
  const sdescs = get(hit, 'sdescs', {});
  const features = get(hit, 'features', []);
  const lowerCaseFeatures = features.map(feature => feature.toLowerCase());
  const platforms = hit.platforms;

  let freeToPlayTag = false;
  if (includes(lowerCaseFeatures, 'free to play')) {
    freeToPlayTag = true;
  }

  const onLinkClick = () => {
    if (place === 'Mega Menu') {
      clickFunction();
    }

    if (place === 'Checkout Modal') {
      store.dispatch(toggleCheckoutModal());
    }
  };

  let price = hit.price;
  let validDrm = '';
  let filteredDrm = [];
  if (hit && hit.drm && !flashDeal && !notAlgolia) filteredDrm = hit.drm.filter(d => d !== 'voucher' && d !== 'drm free');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];
  if (flashDeal || notAlgolia) {
    const validPlatforms = Object.keys(hit.drm).filter(d => hit.drm[d]);
    filteredDrm = validPlatforms.filter(platform => platform !== 'voucher');
    validDrm = filteredDrm[0];

    startingDate = hit.available_valid_from / 1000;
    endingDate = hit.available_valid_until / 1000;

    OS = [];
    if (platforms && platforms.windows) OS.push('windows');
    if (platforms && platforms.mac) OS.push('mac');
    if (platforms && platforms.linux) OS.push('linux');
  }

  if (notAlgolia) {
    const formattedPrice = {};
    Object.keys(price).forEach((currency) => {
      if (!formattedPrice[currency]) {
        formattedPrice[currency] = price[currency] / 100;
      }
    });
    price = formattedPrice;
  }

  let discountPercent = hit.discount_percent;

  if (hit.type === 'pick-and-mix') {
    discountPercent = hit.pnm_saving;
  }

  OS.sort().reverse();

  const name = FormatProductName(hit.name, hit.type);

  let cardTheme = 'dark';
  if (hitCardLightTheme || siteTheme === 'light') cardTheme = 'light';
  if (hitCardDarkTheme) cardTheme = 'dark';

  let productCoverSizes = imageSizes(lgSize, mdSize, smSize, xsSize);

  if (sizesOverride) productCoverSizes = sizesOverride;

  let cardType = 'standard';
  if (hit.display_type === 'comic-bundle' || hit.display_type === 'comic') cardType = 'comic';
  if (hit.display_type === 'book-bundle' || hit.display_type === 'book') cardType = 'book';
  if (hit.display_type === 'elearning-bundle' || hit.display_type === 'elearning') cardType = 'elearning';
  if (hit.display_type === 'audio-bundle' || hit.display_type === 'audio') cardType = 'audio';
  if (hit.display_type === 'software-bundle' || hit.display_type === 'software') cardType = 'software';
  if (hit.display_type === 'graphic-novel') cardType = 'graphic-novel';

  let showAddButton = false;
  if (!hit.tiered && !hit.presale && !hit.free_to_play && !hit.pay_what_you_want && hit.type !== 'pick-and-mix') {
    showAddButton = true;
  }

  let coverType = '';
  if (hit.cover_type) coverType = hit.cover_type;
  if (coverTypeOverride) coverType = coverTypeOverride;

  let _labelOverride = labelOverride;

  if (!_labelOverride) {
    if (inLibrary && authenticated && !hit.mystery) {
      _labelOverride = 'inLibrary';
    } else if (inSteamLibrary && authenticated && !hit.mystery) {
      _labelOverride = 'inSteamLibrary';
    }
  }

  const daysLeft = differenceInDays(new Date(endingDate * 1000), new Date());
  const showTimer = !isSrpBundle && (hit.type === 'bundle' || hit.type === 'pick-and-mix') && daysLeft < 90;

  const getPopupWidth = (cardWidth) => {
    if (isLg) return POPUP_WIDTH_LG;
    const minWidth = isBigCard ? MIN_POPUP_WIDTH_BIG_CARD : MIN_POPUP_WIDTH;
    return Math.round(Math.max(minWidth, cardWidth + MIN_POPUP_OVERHANG));
  };

  // The popup is centred on the card. `left` is worked out here because it has to be clamped to
  // the viewport, but the vertical centring is left to CSS (translateY(-50%)) so that it doesn't
  // depend on the popup's height — that varies with the width of the screenshot carousel and
  // with how much content each product has.
  const positionPopup = (cardRect, popupWidth) => {
    const edgePadding = 8;

    const top = cardRect.top + window.scrollY + (cardRect.height / 2);
    let left = (cardRect.left + window.scrollX + (cardRect.width / 2)) - (popupWidth / 2);

    const minLeft = window.scrollX + edgePadding;
    const maxLeft = (window.scrollX + window.innerWidth) - popupWidth - edgePadding;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    setPopupPosition({ top: `${top}px`, left: `${left}px` });
  };


  const handleHoverStart = () => {
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      const cardRect = cardRef?.current?.getBoundingClientRect();
      if (!cardRect) return;

      const popupWidth = getPopupWidth(cardRect.width);

      showPopup(hit, {
        isBigCard,
        isLg,
        labelOverride,
        productCoverSizes,
        coverType,
        flashDeal,
        showFlashDealBar,
        hitCardLightTheme,
        hitCardDarkTheme,
        clickFunction,
        place,
        adaptiveSize,
        popupWidth,
        bundleCoverGrid,
      });
      positionPopup(cardRect, popupWidth);
    }, 200);
  };

  const handleHoverEnd = () => {
    clearTimeout(hoverTimeout.current);
    scheduleHide();
  };
  return (
    <React.Fragment>
      <Col
        tag={hitCardContainerTag}
        lg={lgSize}
        md={mdSize}
        sm={smSize}
        xs={xsSize}
        className={classNames(
          'HitCardContainer',
          { classes: classes !== '' },
        )}
      >
        <div
          ref={cardRef}
          className={classNames(
            'HitCard HitCard--popup',
            { 'HitCard--dark': cardTheme === 'dark' },
            { 'HitCard--light': cardTheme === 'light' },
            { 'HitCard--light': cardTheme === 'light' },
            { 'big-hit-card': bigCard },
            { 'flash-hit-card': flashDeal || showFlashDealBar },
            'faux-block-link',
          )}
          onMouseEnter={handleHoverStart}
          onMouseLeave={handleHoverEnd}
        >
          <CardLabel
            slug={hit.slug}
            bestDiscount={hit.best_ever}
            preorderDate={hit.release_date}
            startingDate={startingDate}
            endingDate={endingDate}
            type={hit.type}
            displayType={hit.display_type}
            presale={hit.presale}
            discountPercent={hit.discount_percent}
            flashDealCard={flashDeal || showFlashDealBar}
            noReleaseDate={hit.no_release_date}
            collections={hit.collections || []}
            systemDiscount={hit.system_discount}
            labelOverride={_labelOverride}
            earlyAccessDate={hit.preorder_playable_from}
            isStarDeal={hit.star_deal}
          />


          <div key={hit.product_id} className="HitCard__main">
            <HitCardInCart slug={hit.slug} />
            <ProductCover
              cover={hit.cover}
              portraitCover={hit.portrait_cover}
              alt={hit.cover_alt_text || `Product cover for ${name}`}
              sizesOverride={productCoverSizes}
              displayType={hit.display_type}
              coverType={coverType}
              lazy={lazy || place === 'Mega Menu'}
              adaptiveSize={adaptiveSize}
            />

            <div className="hitCardStripe">
              {bigCard && !hideDesc &&
                <div className="big-card-sdesc d-flex align-items-center">
                  {sdescs[locale] || hit.sdesc}
                </div>
              }

              <div className="hitCardStripe__content">
                <div className="hitCardStripe__content__purchase">
                  {!showTimer &&
                    <Link to={HitLinkTo(hit)} className="hitCardStripe__content__purchase__price faux-block-link__overlay-link" onClick={onLinkClick}>
                      {hit.presale && (!freeToPlayTag || hit.free_to_play) &&
                        <div className="hitCardStripe__content__purchase__price__text">
                          <FormattedMessage id="popuphitcard.availablesoon" defaultMessage="Available Soon" />
                        </div>
                      }

                      {(hit.free_to_play || (hit.presale && freeToPlayTag)) &&
                        <div className="hitCardStripe__content__purchase__price__text">
                          <FormattedMessage id="popuphitcard.freetoplay" defaultMessage="Free to Play" />
                        </div>
                      }

                      {!hit.presale && !hit.free_to_play && !hit.hide_price &&
                        <PriceContainer
                          discountPercent={discountPercent}
                          discountPercentOverride={discountPercentOverride}
                          drm={validDrm}
                          fullPrice={hit.fullPrice}
                          giftButton={giftButton}
                          hideDiscount={hit.hide_discount}
                          lightTheme={hitCardLightTheme}
                          mystery={hit.mystery}
                          payWhatYouWant={hit.pay_what_you_want}
                          price={price}
                          showAsterisk={!!hit.addVoucher}
                          slug={hit.slug}
                          tiered={hit.tiered}
                          type={hit.type}
                        />
                      }
                    </Link>
                  }

                  {showTimer &&
                    <Link to={HitLinkTo(hit)} className="faux-block-link__overlay-link" onClick={onLinkClick}>
                      <NewCountdownTimer
                        cardTimer
                        value={endingDate * 1000}
                      />
                    </Link>
                  }

                  {showAddButton && isBelowLg &&
                    <AddToCartBtn
                      newIcon
                      showInCartLink
                      hideButtonText
                      id={hit.product_id}
                      slug={hit.slug}
                      voucherToAdd={hit.addVoucher}
                    />
                  }
                </div>

                {isAboveSm &&
                  <div className="hitCardStripe__content__icons">
                    {(validDrm || cardType === 'standard') &&
                      <React.Fragment>
                        {validDrm &&
                          <Drm drmType={validDrm} colorOverride={cardTheme === 'dark' ? 'light' : 'dark'} />
                        }
                        {hit.display_type === 'dlc' &&
                          <DlcBadge />
                        }
                      </React.Fragment>
                    }
                    {!validDrm && (cardType === 'comic' || cardType === 'book' || cardType === 'elearning' || cardType === 'audio' || cardType === 'software' || cardType === 'graphic-novel') &&
                      <div className="hitCardStripe__content__icons__nonGame">
                        {cardType === 'comic' &&
                          <>
                            <FontAwesomeIcon icon={faBookOpen} />
                            <FormattedMessage id="popuphitcard.comic-bundle" defaultMessage="Comic" />
                          </>
                        }
                        {cardType === 'book' &&
                          <>
                            <FontAwesomeIcon icon={faBookOpen} />
                            <FormattedMessage id="popuphitcard.book-bundle" defaultMessage="eBook" />
                          </>
                        }
                        {cardType === 'elearning' &&
                          <>
                            <FontAwesomeIcon icon={faGraduationCap} />
                            <FormattedMessage id="popuphitcard.elearning-bundle" defaultMessage="eLearning" />
                          </>
                        }
                        {cardType === 'audio' &&
                          <>
                            <FontAwesomeIcon icon={faHeadphonesSimple} />
                            <FormattedMessage id="popuphitcard.audio" defaultMessage="Audio" />
                          </>
                        }
                        {cardType === 'software' &&
                          <>
                            <FontAwesomeIcon icon={faLaptopCode} />
                            <FormattedMessage id="popuphitcard.software" defaultMessage="Software" />
                          </>
                        }
                        {cardType === 'graphic-novel' &&
                          <>
                            <FontAwesomeIcon icon={faBookOpen} />
                            <FormattedMessage id="popuphitcard.graphic-novel" defaultMessage="Graphic Novel" />
                          </>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
            {(flashDeal || showFlashDealBar) &&
              <FlashCardProgress startingDate={startingDate} endingDate={endingDate} />
            }
          </div>
        </div>
      </Col>
    </React.Fragment>
  );
}

PopupHitCard.propTypes = {
  bigCard: PropTypes.bool,
  classes: PropTypes.string,
  clickFunction: PropTypes.func,
  // displayName: PropTypes.string,
  flashDeal: PropTypes.bool,
  notAlgolia: PropTypes.bool,
  giftButton: PropTypes.bool,
  hideDesc: PropTypes.bool,
  // inCart: PropTypes.bool,
  inLibrary: PropTypes.bool,
  inSteamLibrary: PropTypes.bool,
  hit: PropTypes.shape({}),
  hitCardContainerTag: PropTypes.string,
  hitCardDarkTheme: PropTypes.bool,
  hitCardLightTheme: PropTypes.bool,
  // hitCardNameTag: PropTypes.string,
  labelOverride: PropTypes.string,
  lgSize: PropTypes.number,
  lazy: PropTypes.bool,
  mdSize: PropTypes.number,
  place: PropTypes.string,
  showFlashDealBar: PropTypes.bool,
  sizesOverride: PropTypes.string,
  smSize: PropTypes.number,
  // wishlistId: PropTypes.string,
  // wishlistSlugs: PropTypes.arrayOf(PropTypes.string),
  xsSize: PropTypes.number,
  authenticated: PropTypes.bool,
  coverTypeOverride: PropTypes.string,
  adaptiveSize: PropTypes.bool,
  bundleCoverGrid: PropTypes.bool,
};

PopupHitCard.defaultProps = {
  bigCard: false,
  classes: '',
  clickFunction: () => { },
  displayName: '',
  flashDeal: false,
  notAlgolia: false,
  giftButton: false,
  hideDesc: false,
  inCart: false,
  inLibrary: false,
  inSteamLibrary: false,
  hit: {},
  hitCardContainerTag: 'div',
  hitCardDarkTheme: false,
  hitCardLightTheme: false,
  hitCardNameTag: 'div',
  labelOverride: '',
  lgSize: 3,
  lazy: false,
  location: {},
  mdSize: 4,
  place: '',
  showFlashDealBar: false,
  sizesOverride: null,
  smSize: 4,
  wishlistId: '',
  wishlistSlugs: [],
  xsSize: 6,
  authenticated: false,
  coverTypeOverride: null,
  adaptiveSize: false,
  bundleCoverGrid: false,
};

const mapStateToProps = ({
  auth: { _id: userId, authenticated },
  wishlist: { allSlugs: wishlistSlugs },
  cart: { allSlugs: cartSlugs },
  siteTheme,
  userLibrary,
  steamLibrary,
  router: { location },

}, { hit: { name, slug } }) => ({
  userId,
  authenticated,
  wishlistSlugs,
  inCart: cartSlugs.includes(slug),
  siteTheme,
  inLibrary: userLibrary.includes(name),
  inSteamLibrary: steamLibrary.includes(name),
  pathname: location.pathname,
});

export default connect(mapStateToProps)(PopupHitCard);
